import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DayPicker } from 'react-day-picker';
import { __ } from '@wordpress/i18n';
import MonthPicker from './MonthPicker.js';

const DatePickerWrapper = ({
	datePicker,
	datePickerInput,
	datePickerButton,
	monthView,
	initialMonth,
	initialYear,
	currentMonth,
	currentYear,
	lastMonth,
	lastYear,
	showPastEvents,
}) => {
	const [selectedDays, setSelectedDays] = useState(null);
	const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
	const datePickerRef = useRef(null);

	const handleDayPickerSelect = (currentSelectedDays) => {
		setSelectedDays(currentSelectedDays);

		const range = [];

		if (currentSelectedDays?.from) {
			range.push(currentSelectedDays.from);
		}

		if (
			currentSelectedDays?.to &&
			currentSelectedDays.from.getTime() !==
				currentSelectedDays.to.getTime()
		) {
			range.push(currentSelectedDays.to);
		}

		updateInputValue(datePickerInput, range, datePickerButton);
	};

	const updateInputValue = useCallback(
		(input, days, button) => {
			const formattedDates = days
				.filter(Boolean)
				.map((d) => formatDate(d))
				.join(' - ');

			input.value = formattedDates;

			const event = new Event('input');
			input.dispatchEvent(event);

			let formattedButtonDates;

			if (monthView && days.length > 0) {
				const firstDay = days[0];

				formattedButtonDates = firstDay.toLocaleDateString('en-US', {
					month: 'long',
					year: 'numeric',
				});
			} else {
				formattedButtonDates = days
					.filter(Boolean)
					.map((d) =>
						d.toLocaleDateString('en-US', {
							month: 'long',
							day: 'numeric',
						})
					)
					.join(' - ');
			}

			button.innerText =
				formattedButtonDates ||
				datePickerButton?.dataset?.today ||
				__('Select Date', 'propel');
		},
		[datePickerButton, monthView]
	);

	const formatDate = (date) => {
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');

		return `${year}-${month}-${day}`;
	};

	useEffect(() => {
		const toggleDatePickerHandler = () => {
			setIsDatePickerOpen((prev) => {
				const newState = !prev;

				if (newState) {
					datePickerButton.classList.add(
						'filter-date__date-picker-button--active'
					);

					datePickerButton.setAttribute('aria-expanded', 'true');
				} else {
					datePickerButton.classList.remove(
						'filter-date__date-picker-button--active'
					);

					datePickerButton.setAttribute('aria-expanded', 'false');
				}

				return newState;
			});
		};

		const resetDatePickerHandler = () => {
			if (monthView) {
				const initialDateFrom = new Date(
					currentYear,
					currentMonth - 1,
					1
				);

				const initialDateTo = new Date(currentYear, currentMonth, 0);

				setSelectedDays({ from: initialDateFrom, to: initialDateTo });

				updateInputValue(
					datePickerInput,
					[initialDateFrom, initialDateTo],
					datePickerButton
				);
			} else {
				setSelectedDays([]);
				updateInputValue(datePickerInput, [], datePickerButton);
			}
		};

		const updateDatePickerButtonText = (e) => {
			if (
				monthView ||
				!e?.detail?.postsContainer ||
				datePickerInput.value
			) {
				return;
			}

			const eventCards = e.detail.postsContainer.querySelectorAll(
				'.event-card[data-filter-date]'
			);

			if (eventCards.length > 0) {
				const firstEventCard = eventCards[0];
				const lastEventCard = eventCards[eventCards.length - 1];

				if (e?.detail?.pastPage > 0) {
					// Past batches render most-recent-first; show the span chronologically.
					if (
						firstEventCard.dataset.filterDate ===
						lastEventCard.dataset.filterDate
					) {
						datePickerButton.innerText = `${firstEventCard.dataset.filterDate}`;
					} else {
						datePickerButton.innerText = `${lastEventCard.dataset.filterDate} - ${firstEventCard.dataset.filterDate}`;
					}
				} else if (
					!e?.detail?.currentPage ||
					e.detail.currentPage === 1
				) {
					datePickerButton.innerText = `${__('Now', 'propel')} - ${lastEventCard.dataset.filterDate}`;
				} else if (
					lastEventCard.dataset.filterDate ===
					firstEventCard.dataset.filterDate
				) {
					datePickerButton.innerText = `${firstEventCard.dataset.filterDate}`;
				} else {
					datePickerButton.innerText = `${firstEventCard.dataset.filterDate} - ${lastEventCard.dataset.filterDate}`;
				}
			}
		};

		const handleClickOutside = (e) => {
			if (
				datePickerRef?.current?.parentElement &&
				!datePickerRef.current.parentElement.contains(e.target) &&
				!e.target.classList.contains('filter-date__date-picker-button')
			) {
				closeDatePickerHandler();
			}
		};

		const closeDatePickerHandler = () => {
			setIsDatePickerOpen(false);

			datePickerButton.classList.remove(
				'filter-date__date-picker-button--active'
			);

			datePickerButton.setAttribute('aria-expanded', 'false');
		};

		datePicker.addEventListener(
			'propel-toggle-date-picker',
			toggleDatePickerHandler
		);

		datePicker.addEventListener(
			'propel-reset-date-picker',
			resetDatePickerHandler
		);

		if (!monthView) {
			window.addEventListener(
				'propel-filters-cleared',
				resetDatePickerHandler
			);
		}

		window.addEventListener(
			'propel-filters-render-posts',
			updateDatePickerButtonText
		);

		if (isDatePickerOpen) {
			document.addEventListener('click', handleClickOutside);

			window.addEventListener(
				'propel-filters-toggled',
				closeDatePickerHandler
			);
		}

		return () => {
			datePicker.removeEventListener(
				'propel-toggle-date-picker',
				toggleDatePickerHandler
			);

			datePicker.removeEventListener(
				'propel-reset-date-picker',
				resetDatePickerHandler
			);

			if (!monthView) {
				window.removeEventListener(
					'propel-filters-cleared',
					resetDatePickerHandler
				);
			}

			window.removeEventListener(
				'propel-filters-render-posts',
				updateDatePickerButtonText
			);

			document.removeEventListener('click', handleClickOutside);

			window.removeEventListener(
				'propel-filters-toggled',
				closeDatePickerHandler
			);
		};
	}, [
		datePicker,
		datePickerButton,
		datePickerInput,
		updateInputValue,
		isDatePickerOpen,
		currentMonth,
		currentYear,
		monthView,
	]);

	let captionLayout = 'dropdown';

	const components = {
		Chevron: () => <span className="icon icon-chev-expand"></span>,
	};

	const classNames = {
		root: 'filter-datepicker',
		months: 'filter-datepicker__months',
		month: 'filter-datepicker__month',
		month_caption: 'filter-datepicker__month-caption',
		month_grid: 'filter-datepicker__month-grid',
		caption_label: 'filter-datepicker__caption-label',
		chevron: 'filter-datepicker__chevron',
		nav: 'filter-datepicker__nav',
		button_previous: 'filter-datepicker__nav-button--previous',
		button_next: 'filter-datepicker__nav-button--next',
		day: 'filter-datepicker__day',
		day_button: 'filter-datepicker__day-button',
		today: 'filter-datepicker__day--today',
		week: 'filter-datepicker__week',
		weeks: 'filter-datepicker__weeks',
		weekdays: 'filter-datepicker__weekdays',
		weekday: 'filter-datepicker__weekday',
		week_number: 'filter-datepicker__week-number',
		week_number_header: 'filter-datepicker__week-number-header',
		footer: 'filter-datepicker__footer',
		dropdown: 'filter-datepicker__dropdown',
		dropdown_root: 'filter-datepicker__dropdown-root',
		dropdowns: 'filter-datepicker__dropdowns',
		months_dropdown: 'filter-datepicker__dropdown--months',
		years_dropdown: 'filter-datepicker__dropdown--years',
		range_end: 'filter-datepicker__day--range-end',
		range_middle: 'filter-datepicker__day--range-middle',
		range_start: 'filter-datepicker__day--range-start',
		selected: 'filter-datepicker__day--selected',
	};

	if (monthView) {
		captionLayout = 'label';

		components.MonthGrid = () => (
			<MonthPicker
				classNames={classNames}
				select={handleDayPickerSelect}
				selected={selectedDays}
				initialMonth={initialMonth}
				initialYear={initialYear}
				currentMonth={currentMonth}
				currentYear={currentYear}
				datePicker={datePicker}
				showPastEvents={showPastEvents}
			/>
		);

		components.Nav = () => null;
		components.CaptionLabel = () => null;
		components.MonthCaption = () => null;
	}

	const startMonth =
		'true' === showPastEvents
			? undefined
			: new Date(new Date().getFullYear(), new Date().getMonth());

	return (
		<div
			ref={datePickerRef}
			style={{ display: isDatePickerOpen ? 'block' : 'none' }}
		>
			<DayPicker
				classNames={classNames}
				mode="range"
				selected={selectedDays}
				captionLayout={captionLayout}
				onSelect={handleDayPickerSelect}
				components={components}
				startMonth={startMonth}
				endMonth={new Date(lastYear, lastMonth - 1)}
			/>
		</div>
	);
};

export default DatePickerWrapper;
